import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { 
  LogOut,
  Users,
  Wallet,
  Megaphone,
  FileText,
  ChevronRight,
  Footprints,
  TrendingUp,
  Coins,
  Sparkles,
  ArrowUpRight,
  Download,
  ArrowDownRight,
  Clock,
  Zap,
  Star,
  Gift,
  Trophy,
  Rocket,
  UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import NotificationBell from "../components/NotificationBell";
import ThemeToggle from "../components/ThemeToggle";
import { useTheme } from "../components/ThemeProvider";
import { CollaboratorRequestForm, CollaboratorRequestsList } from "../components/CollaboratorRequests";
import DashboardCharts from "../components/DashboardCharts";

const FRASI_MOTIVAZIONALI = [
  "Ogni pratica completata è un passo verso il successo! 🚀",
  "Il tuo impegno fa la differenza ogni giorno! 💪",
  "Grandi risultati richiedono grande dedizione! ⭐",
  "Oggi è il giorno perfetto per fare la differenza! 🌟",
  "Il successo è la somma di piccoli sforzi ripetuti! 🎯",
  "La qualità del tuo lavoro parla per te! 👏",
  "Ogni cliente soddisfatto è una vittoria! 🏆",
  "Continua così, stai facendo un ottimo lavoro! 💎",
  "Il tuo contributo è prezioso per il team! 🤝",
  "Trasforma ogni sfida in un'opportunità! 🌈",
  "La perseveranza porta sempre risultati! 🔥",
  "Sei più vicino ai tuoi obiettivi di quanto pensi! 📈",
  "Ogni giorno è una nuova opportunità di crescita! 🌱",
  "Il lavoro di squadra rende il sogno realtà! ✨",
  "La tua professionalità è il nostro punto di forza! 💼"
];

const MACRO_AREE = [
  { 
    value: "ORTOPEDIA", 
    label: "Ortopedia", 
    description: "Scarpe ortopediche, busti, ausili",
    gradient: "from-blue-500 to-blue-600",
    hoverGradient: "from-blue-400 to-blue-500",
    bgLight: "bg-blue-50",
    textColor: "text-blue-600",
    shadowColor: "shadow-blue-500/30",
    icon: "🦴"
  },
  { 
    value: "FINANZIARIO", 
    label: "Finanziario", 
    description: "Pratiche finanziarie e prestiti",
    gradient: "from-emerald-500 to-green-600",
    hoverGradient: "from-emerald-400 to-green-500",
    bgLight: "bg-green-50",
    textColor: "text-green-600",
    shadowColor: "shadow-green-500/30",
    icon: "💰"
  },
  { 
    value: "CAF", 
    label: "CAF", 
    description: "Dichiarazioni, ISEE, 730",
    gradient: "from-purple-500 to-violet-600",
    hoverGradient: "from-purple-400 to-violet-500",
    bgLight: "bg-purple-50",
    textColor: "text-purple-600",
    shadowColor: "shadow-purple-500/30",
    icon: "📋"
  },
  { 
    value: "PATRONATO", 
    label: "Patronato", 
    description: "Pensioni, invalidità, assistenza",
    gradient: "from-orange-500 to-amber-600",
    hoverGradient: "from-orange-400 to-amber-500",
    bgLight: "bg-orange-50",
    textColor: "text-orange-600",
    shadowColor: "shadow-orange-500/30",
    icon: "🏛️"
  },
  { 
    value: "LEGALE", 
    label: "Legale", 
    description: "Consulenze e pratiche legali",
    gradient: "from-red-500 to-rose-600",
    hoverGradient: "from-red-400 to-rose-500",
    bgLight: "bg-red-50",
    textColor: "text-red-600",
    shadowColor: "shadow-red-500/30",
    icon: "⚖️"
  },
  { 
    value: "ASSICURAZIONE", 
    label: "Assicurazione", 
    description: "Polizze e pratiche assicurative",
    gradient: "from-teal-500 to-cyan-600",
    hoverGradient: "from-teal-400 to-cyan-500",
    bgLight: "bg-teal-50",
    textColor: "text-teal-600",
    shadowColor: "shadow-teal-500/30",
    icon: "🛡️"
  },
  { 
    value: "LUCE_E_GAS", 
    label: "Luce e Gas", 
    description: "Contratti luce e gas",
    gradient: "from-yellow-500 to-orange-500",
    hoverGradient: "from-yellow-400 to-orange-400",
    bgLight: "bg-yellow-50",
    textColor: "text-yellow-600",
    shadowColor: "shadow-yellow-500/30",
    icon: "⚡"
  }
];

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [hoveredArea, setHoveredArea] = useState(null);
  const [walletHovered, setWalletHovered] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  
  const fraseMotivazionale = useMemo(() => {
    return FRASI_MOTIVAZIONALI[Math.floor(Math.random() * FRASI_MOTIVAZIONALI.length)];
  }, []);
  
  const visibleAreas = user?.role === "admin" 
    ? MACRO_AREE 
    : user?.role === "titolare" && user?.macro_area
      ? MACRO_AREE.filter(a => a.value === user.macro_area)
      : user?.role === "collaboratore" && Array.isArray(user?.macro_aree_visibili)
        ? MACRO_AREE.filter(a => user.macro_aree_visibili.includes(a.value))
        : MACRO_AREE;
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, walletRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/stats/by-area`, { withCredentials: true }),
        axios.get(`${API}/wallet`, { withCredentials: true }),
        axios.get(`${API}/wallet/transactions`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setWallet(walletRes.data);
      setTransactions(transactionsRes.data || []);
    } catch (error) {
      console.error("Errore caricamento:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/login");
    } catch (error) {
      navigate("/login");
    }
  };

  const handleAreaClick = (areaValue) => {
    navigate(`/area/${areaValue}`);
  };

  const getRoleBadge = () => {
    switch (user?.role) {
      case "admin":
        return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Admin</span>;
      case "titolare":
        return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Titolare</span>;
      default:
        return <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">Collaboratore</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const totalOrders = Object.values(stats).reduce((acc, s) => acc + (s.totale || 0), 0);
  const pendingOrders = Object.values(stats).reduce((acc, s) => acc + (s.richiesta || 0), 0);
  const completedOrders = Object.values(stats).reduce((acc, s) => acc + (s.consegnato || 0), 0);
  const walletBalance = wallet?.balance || 0;

  return (
    <div className="min-h-screen dynamic-bg dark:bg-slate-950" data-testid="dashboard">
      {/* Floating Orbs Background */}
      <div className="floating-orb floating-orb-1"></div>
      <div className="floating-orb floating-orb-2"></div>
      <div className="floating-orb floating-orb-3"></div>
      
      {/* Modern Tech Header */}
      <header className="sticky top-0 z-50">
        {/* Gradient line accent */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        
        <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-900/90 border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg shadow-slate-900/5 dark:shadow-slate-950/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo Section */}
              <div className="flex items-center gap-4">
                <div className="relative group cursor-pointer" onClick={() => navigate("/dashboard")}>
                  {/* Animated ring */}
                  <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-blue-500 via-emerald-500 to-blue-500 opacity-0 group-hover:opacity-60 blur-md transition-all duration-500"></div>
                  
                  {/* Logo container */}
                  <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300 group-hover:scale-105">
                    <Footprints className="w-5 h-5 text-white" />
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-black bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 dark:from-white dark:via-slate-200 dark:to-white bg-clip-text text-transparent tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      OrthoTrack
                    </h1>
                    <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-500 to-emerald-500 text-white shadow-sm">
                      PRO
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-mono tracking-wider">v1.0 - 14/02/2026</p>
                </div>
              </div>
              
              {/* Center Navigation - Only on larger screens */}
              <nav className="hidden lg:flex items-center gap-1 bg-slate-100/80 dark:bg-slate-800/80 rounded-full p-1">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-1.5 rounded-full text-sm font-medium bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm transition-all"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate("/clients")}
                  className="px-4 py-1.5 rounded-full text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all"
                >
                  Clienti
                </button>
                {user?.role === "admin" && (
                  <button
                    onClick={() => navigate("/collaborators")}
                    className="px-4 py-1.5 rounded-full text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all"
                  >
                    Team
                  </button>
                )}
              </nav>
              
              {/* Right Section */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Quick Action Buttons */}
                <div className="hidden sm:flex items-center gap-1 mr-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/asl-mandates")}
                    className="relative w-9 h-9 rounded-xl text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/80 dark:hover:bg-blue-900/30 transition-all duration-300 group"
                    title="Mandati"
                  >
                    <Download className="w-4 h-4 transition-transform group-hover:scale-110" />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/marketing")}
                    className="relative w-9 h-9 rounded-xl text-slate-500 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50/80 dark:hover:bg-purple-900/30 transition-all duration-300 group"
                    title="Materiale Marketing"
                  >
                    <Megaphone className="w-4 h-4 transition-transform group-hover:scale-110" />
                  </Button>
                </div>
                
                {/* Notification Bell */}
                <NotificationBell />
                
                {/* Theme Toggle */}
                <ThemeToggle />
                
                {/* Divider */}
                <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700 mx-2"></div>
                
                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100/80 dark:hover:bg-slate-800/80 transition-all group">
                      {/* Avatar with status indicator */}
                      <div className="relative">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-[2px] group-hover:shadow-lg group-hover:shadow-purple-500/25 transition-all">
                          <div className="w-full h-full rounded-[10px] bg-white dark:bg-slate-900 flex items-center justify-center">
                            <span className="text-sm font-bold bg-gradient-to-br from-blue-600 to-purple-600 bg-clip-text text-transparent">
                              {user?.name?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                        </div>
                        {/* Online indicator */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                      </div>
                      
                      {/* User info */}
                      <div className="hidden sm:flex flex-col items-start">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">{user?.name?.split(' ')[0]}</span>
                        <span className={`text-[10px] font-medium leading-tight ${
                          user?.role === "admin" ? "text-blue-600 dark:text-blue-400" : 
                          user?.role === "titolare" ? "text-amber-600 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"
                        }`}>
                          {user?.role === "admin" ? "Amministratore" : 
                           user?.role === "titolare" ? "Titolare" : "Collaboratore"}
                        </span>
                      </div>
                      
                      {/* Chevron */}
                      <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500 rotate-90 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 p-2 rounded-xl shadow-xl border border-slate-200/50 dark:border-slate-700/50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl">
                    {/* User header in dropdown */}
                    <div className="px-3 py-3 mb-2 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-[2px]">
                          <div className="w-full h-full rounded-[10px] bg-white dark:bg-slate-900 flex items-center justify-center">
                            <span className="text-base font-bold bg-gradient-to-br from-blue-600 to-purple-600 bg-clip-text text-transparent">
                              {user?.name?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">{user?.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{user?.username}</p>
                        </div>
                      </div>
                    </div>
                    
                    {user?.role === "admin" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/clients")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <Users className="w-4 h-4 mr-3 text-blue-500" />
                          <span className="dark:text-slate-200">Scheda Clienti</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/collaborators")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <Users className="w-4 h-4 mr-3 text-purple-500" />
                          <span className="dark:text-slate-200">Gestione Team</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/wallet")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <Wallet className="w-4 h-4 mr-3 text-green-500" />
                          <span className="dark:text-slate-200">Gestione Wallet</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/asl-mandates")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <FileText className="w-4 h-4 mr-3 text-amber-500" />
                          <span className="dark:text-slate-200">Mandati</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/marketing")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <Megaphone className="w-4 h-4 mr-3 text-pink-500" />
                          <span className="dark:text-slate-200">Materiale Marketing</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.role === "titolare" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/clients")} className="rounded-lg py-2.5 cursor-pointer">
                          <Users className="w-4 h-4 mr-3 text-blue-500" />
                          <span>Scheda Clienti</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/wallet")} className="rounded-lg py-2.5 cursor-pointer">
                          <Wallet className="w-4 h-4 mr-3 text-green-500" />
                          <span>Wallet</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/asl-mandates")} className="rounded-lg py-2.5 cursor-pointer">
                          <FileText className="w-4 h-4 mr-3 text-amber-500" />
                          <span>Mandati</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.role === "collaboratore" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/clients")} className="rounded-lg py-2.5 cursor-pointer dark:hover:bg-slate-800">
                          <Users className="w-4 h-4 mr-3 text-blue-500" />
                          <span className="dark:text-slate-200">Scheda Clienti</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Divider */}
                    <div className="my-2 h-px bg-slate-200 dark:bg-slate-700"></div>
                    
                    {/* Logout */}
                    <DropdownMenuItem onClick={handleLogout} className="rounded-lg py-2.5 cursor-pointer text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30">
                      <LogOut className="w-4 h-4 mr-3" />
                      <span className="font-medium">Esci dall'account</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Premium Wallet Card */}
        <div className="mb-8">
          <div 
            className={`relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-500 ${
              walletHovered ? 'scale-[1.01]' : ''
            }`}
            onMouseEnter={() => setWalletHovered(true)}
            onMouseLeave={() => setWalletHovered(false)}
            data-testid="wallet-card"
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #020617 100%)'
            }}
          >
            {/* Animated gradient border */}
            <div className="absolute inset-0 rounded-3xl p-[1px] bg-gradient-to-r from-blue-500 via-emerald-500 to-violet-500 opacity-50">
              <div className="w-full h-full rounded-3xl bg-slate-900"></div>
            </div>
            
            {/* Animated background patterns */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Mesh gradient circles */}
              <div className={`absolute w-[500px] h-[500px] bg-blue-500/20 rounded-full -top-64 -left-64 blur-3xl transition-all duration-1000 ${walletHovered ? 'scale-125 opacity-30' : 'opacity-20'}`}></div>
              <div className={`absolute w-[400px] h-[400px] bg-emerald-500/20 rounded-full -bottom-48 -right-48 blur-3xl transition-all duration-1000 ${walletHovered ? 'scale-125 opacity-30' : 'opacity-20'}`}></div>
              <div className={`absolute w-[300px] h-[300px] bg-violet-500/15 rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 blur-3xl transition-all duration-700 ${walletHovered ? 'scale-150 opacity-25' : 'opacity-15'}`}></div>
              
              {/* Grid pattern */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }}></div>
              
              {/* Floating particles */}
              <div className={`absolute w-1.5 h-1.5 bg-blue-400/60 rounded-full top-8 left-1/4 transition-all duration-1000 ${walletHovered ? 'translate-y-4 opacity-100' : 'opacity-40'}`}></div>
              <div className={`absolute w-2 h-2 bg-emerald-400/50 rounded-full top-16 right-1/3 transition-all duration-1200 ${walletHovered ? '-translate-y-6 opacity-100' : 'opacity-30'}`}></div>
              <div className={`absolute w-1 h-1 bg-violet-400/60 rounded-full bottom-12 left-1/3 transition-all duration-800 ${walletHovered ? 'translate-x-4 opacity-100' : 'opacity-30'}`}></div>
            </div>
            
            <div className="relative z-10 p-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                {/* Left: Balance Section */}
                <div className="flex items-center gap-6">
                  <div className={`relative transition-all duration-500 ${walletHovered ? 'scale-110 rotate-3' : ''}`}>
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 backdrop-blur-sm flex items-center justify-center shadow-2xl">
                      <Coins className="w-10 h-10 text-white/90" />
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 transition-all duration-300 ${walletHovered ? 'scale-125' : ''}`}>
                      <Zap className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  
                  <div className="text-white">
                    <p className="text-white/60 text-sm font-medium mb-1 flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 text-emerald-400 transition-all duration-500 ${walletHovered ? 'rotate-12' : ''}`} />
                      Saldo Disponibile
                    </p>
                    <div className="flex items-baseline gap-3">
                      <span className={`text-6xl font-black tracking-tight transition-all duration-500 font-mono ${walletHovered ? 'scale-105' : ''}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {loading ? "..." : walletBalance}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-xl font-bold text-white/80">Ortho</span>
                        <span className="text-xs text-white/50">crediti</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Center: Stats Circles */}
                <div className="flex items-center gap-6">
                  <div className="text-center transition-all duration-300 stat-wave">
                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center mb-2 mx-auto group hover:bg-white/10 transition-colors">
                      <Trophy className="w-7 h-7 text-amber-400 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="text-2xl font-bold text-white font-mono">{completedOrders}</div>
                    <div className="text-xs text-white/50">Completate</div>
                  </div>
                  
                  <div className="text-center transition-all duration-300 stat-wave">
                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center mb-2 mx-auto group hover:bg-white/10 transition-colors">
                      <Clock className="w-7 h-7 text-blue-400 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="text-2xl font-bold text-white font-mono">{pendingOrders}</div>
                    <div className="text-xs text-white/50">In attesa</div>
                  </div>
                  
                  <div className="text-center transition-all duration-300 stat-wave">
                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center mb-2 mx-auto group hover:bg-white/10 transition-colors">
                      <Star className="w-7 h-7 text-emerald-400 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="text-2xl font-bold text-white font-mono">{totalOrders}</div>
                    <div className="text-xs text-white/50">Totali</div>
                  </div>
                </div>
                
                {/* Right: Action Buttons */}
                <div className="flex flex-col gap-3">
                  {user?.role === "admin" && (
                    <Button 
                      className="bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 transition-all duration-300 hover:scale-105 font-semibold"
                      onClick={() => navigate("/wallet")}
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      Gestisci Crediti
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    className="border border-white/20 text-white hover:bg-white/10 backdrop-blur-sm transition-all duration-300 hover:scale-105 bg-white/5"
                    onClick={() => setShowTransactions(!showTransactions)}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {showTransactions ? 'Nascondi' : 'Transazioni'}
                  </Button>
                </div>
              </div>
              
              {/* Expandable Transactions Section */}
              <div className={`overflow-hidden transition-all duration-500 ${showTransactions ? 'max-h-96 mt-6 pt-6 border-t border-white/10' : 'max-h-0'}`}>
                {transactions.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {transactions.slice(0, 6).map((tx, index) => (
                      <div 
                        key={index} 
                        className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl p-4 transition-all duration-300 hover:bg-white/10 hover:border-white/20"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              tx.type === 'credit' ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'
                            }`}>
                              {tx.type === 'credit' ? (
                                <ArrowDownRight className="w-5 h-5 text-emerald-400" />
                              ) : (
                                <ArrowUpRight className="w-5 h-5 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{tx.description || (tx.type === 'credit' ? 'Ricarica' : 'Utilizzo')}</p>
                              <p className="text-xs text-white/50 font-mono">{formatDate(tx.created_at)}</p>
                            </div>
                          </div>
                          <span className={`font-bold text-lg font-mono ${tx.type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {tx.type === 'credit' ? '+' : '-'}{tx.amount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-white/50">
                    <Coins className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Nessuna transazione recente</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Motivational Quote + Welcome */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Ciao, {user?.name?.split(' ')[0] || 'Utente'}! 👋
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 italic max-w-2xl mx-auto">{fraseMotivazionale}</p>
        </div>
        
        {/* Macro Areas Grid - Super Interactive Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="card-hover p-6 dark:bg-slate-800">
                <Skeleton className="w-16 h-16 rounded-2xl mb-4" />
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))
          ) : (
            visibleAreas.map((area, index) => {
              const areaStats = stats[area.value] || { totale: 0, richiesta: 0 };
              const isHovered = hoveredArea === area.value;
              
              return (
                <div
                  key={area.value}
                  className={`macro-card card-shine cursor-pointer dark:bg-slate-800 dark:border-slate-700 ${
                    isHovered ? `${area.bgLight} ${area.shadowColor} shadow-2xl` : ''
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`
                  }}
                  onMouseEnter={() => setHoveredArea(area.value)}
                  onMouseLeave={() => setHoveredArea(null)}
                  onClick={() => handleAreaClick(area.value)}
                  data-testid={`macro-area-${area.value.toLowerCase()}`}
                >
                  {/* Gradient overlay on hover */}
                  <div 
                    className={`absolute inset-0 bg-gradient-to-br ${area.gradient} transition-opacity duration-500 rounded-2xl ${
                      isHovered ? 'opacity-5' : 'opacity-0'
                    }`}
                  ></div>
                  
                  {/* Animated corner accent */}
                  <div 
                    className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${area.gradient} rounded-bl-full transition-all duration-500 ${
                      isHovered ? 'opacity-20 scale-150' : 'opacity-10 scale-100'
                    }`}
                  ></div>
                  
                  <div className="relative z-10 p-6">
                    {/* Icon with 3D effect */}
                    <div 
                      className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${area.gradient} flex items-center justify-center text-4xl mb-5 transition-all duration-500 ${
                        isHovered 
                          ? 'scale-110 rotate-6 shadow-2xl' 
                          : 'shadow-lg'
                      }`}
                      style={{
                        boxShadow: isHovered 
                          ? `0 20px 40px -10px ${area.gradient.includes('blue') ? 'rgba(59, 130, 246, 0.4)' : 
                              area.gradient.includes('green') || area.gradient.includes('emerald') ? 'rgba(34, 197, 94, 0.4)' :
                              area.gradient.includes('purple') || area.gradient.includes('violet') ? 'rgba(139, 92, 246, 0.4)' :
                              area.gradient.includes('orange') || area.gradient.includes('amber') ? 'rgba(249, 115, 22, 0.4)' :
                              area.gradient.includes('red') || area.gradient.includes('rose') ? 'rgba(239, 68, 68, 0.4)' :
                              'rgba(20, 184, 166, 0.4)'}`
                          : 'none'
                      }}
                    >
                      <span className={`transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}>
                        {area.icon}
                      </span>
                      
                      {/* Pulse effect when hovered */}
                      {isHovered && (
                        <div className="absolute inset-0 rounded-2xl animate-ping bg-white/20"></div>
                      )}
                    </div>
                    
                    {/* Title with gradient on hover */}
                    <h3 className={`text-2xl font-bold mb-2 transition-all duration-300 ${
                      isHovered ? `${area.textColor}` : 'text-slate-900 dark:text-white'
                    }`}>
                      {area.label}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">{area.description}</p>
                    
                    {/* Stats with animation */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-5">
                        <div className={`transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}>
                          <div className={`text-4xl font-black transition-colors duration-300 ${
                            isHovered ? area.textColor : 'text-slate-900 dark:text-white'
                          }`}>
                            {areaStats.totale}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">Ordini totali</div>
                        </div>
                        {areaStats.richiesta > 0 && (
                          <div className="pl-5 border-l-2 border-slate-200 dark:border-slate-600">
                            <div className="text-2xl font-bold text-amber-500 animate-pulse">
                              {areaStats.richiesta}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">In attesa</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Arrow button with glow */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isHovered 
                          ? `bg-gradient-to-br ${area.gradient} text-white shadow-xl scale-110` 
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                      }`}>
                        <ChevronRight className={`w-6 h-6 transition-transform duration-300 ${
                          isHovered ? 'translate-x-1' : ''
                        }`} />
                      </div>
                    </div>
                    
                    {/* Bottom accent line */}
                    <div 
                      className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${area.gradient} transition-all duration-500 rounded-b-2xl ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                      }`}
                    ></div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Quick Stats for Admin */}
        {user?.role === "admin" && (
          <div className="mt-10 p-6 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 shadow-lg">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/10 to-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white tracking-tight">Statistiche Generali</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700/50 dark:to-slate-800/50 rounded-xl hover:scale-105 transition-transform cursor-pointer hover:shadow-lg border border-slate-200/50 dark:border-slate-600/50">
                <div className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{totalOrders}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Ordini Totali</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl hover:scale-105 transition-transform cursor-pointer hover:shadow-lg border border-amber-200/50 dark:border-amber-800/50">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 font-mono">{pendingOrders}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">In Attesa</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl hover:scale-105 transition-transform cursor-pointer hover:shadow-lg border border-emerald-200/50 dark:border-emerald-800/50">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 font-mono">{completedOrders}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Completati</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl hover:scale-105 transition-transform cursor-pointer hover:shadow-lg border border-blue-200/50 dark:border-blue-800/50">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 font-mono">{visibleAreas.length}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Aree Attive</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Advanced Charts Section */}
        {(user?.role === "admin" || user?.role === "titolare") && (
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/10 to-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-lg tracking-tight">Analisi Avanzata</h3>
            </div>
            <DashboardCharts />
          </div>
        )}
        
        {/* Collaborator Request Section for Titolare */}
        {user?.role === "titolare" && (
          <div className="mt-10 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-slate-900 dark:text-white">Gestione Collaboratori</h3>
            </div>
            
            {/* Request Form */}
            <CollaboratorRequestForm />
            
            {/* My Requests List */}
            <div className="p-6 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-4">Le tue segnalazioni</h4>
              <CollaboratorRequestsList isAdmin={false} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
